import { useState } from 'react';
import { motion } from 'framer-motion';
import { Bookmark, ExternalLink, Eye, Clock, Zap } from 'lucide-react';

const DealCard = ({ deal, category, onSave, onWatch }) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  
  const categoryColor = category?.color || '#F472B6';
  const timeLeft = deal.expires_at ? getTimeLeft(deal.expires_at) : null;
  
  // Calculate discount percentage
  const discount = deal.original_price && deal.deal_price 
    ? Math.round(((deal.original_price - deal.deal_price) / deal.original_price) * 100)
    : null;

  return (
    <motion.div 
      className="relative rounded-2xl overflow-hidden bg-card border-2 transition-all hover:shadow-lg group"
      style={{ borderColor: `${categoryColor}40` }}
      whileHover={{ y: -4, borderColor: categoryColor }}
      onHoverStart={onWatch}
      data-testid={`deal-card-${deal.drop_id}`}
    >
      {/* Category Badge */}
      <div 
        className="absolute top-3 left-3 z-10 px-3 py-1 rounded-full text-xs font-medium text-black"
        style={{ backgroundColor: categoryColor }}
      >
        {category?.icon} {category?.name}
      </div>

      {/* Flash Deal Pulse */}
      {deal.category === 'flash' && (
        <motion.div 
          className="absolute top-3 right-3 z-10 w-3 h-3 rounded-full bg-red-500"
          animate={{ scale: [1, 1.3, 1], opacity: [1, 0.7, 1] }}
          transition={{ duration: 1, repeat: Infinity }}
        />
      )}

      {/* Image */}
      <div className="aspect-square bg-muted relative overflow-hidden">
        {deal.image_url ? (
          <>
            {!imageLoaded && (
              <div className="absolute inset-0 bg-muted animate-pulse" />
            )}
            <img 
              src={deal.image_url} 
              alt={deal.product_name}
              className={`w-full h-full object-cover transition-opacity ${imageLoaded ? 'opacity-100' : 'opacity-0'}`}
              onLoad={() => setImageLoaded(true)}
            />
          </>
        ) : (
          <div className="w-full h-full flex items-center justify-center text-4xl">
            {category?.icon || '🛍️'}
          </div>
        )}

        {/* Discount Badge */}
        {discount && (
          <motion.div 
            className="absolute bottom-3 right-3 px-3 py-1.5 rounded-full bg-green-500 text-white font-bold text-sm"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: 'spring', bounce: 0.5 }}
          >
            -{discount}%
          </motion.div>
        )}

        {/* Sold Out Overlay */}
        {deal.is_sold_out && (
          <motion.div 
            className="absolute inset-0 bg-black/70 flex items-center justify-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <span className="text-white font-bold text-xl rotate-[-15deg]">SOLD OUT</span>
          </motion.div>
        )}
      </div>

      {/* Content */}
      <div className="p-4">
        <h3 className="font-semibold truncate mb-1">{deal.product_name}</h3>
        <p className="text-sm text-muted-foreground truncate mb-3">{deal.brand}</p>

        {/* Price */}
        <div className="flex items-center gap-2 mb-3">
          {deal.deal_price && (
            <span className="text-xl font-bold" style={{ color: categoryColor }}>
              ${deal.deal_price}
            </span>
          )}
          {deal.original_price && (
            <span className="text-sm text-muted-foreground line-through">
              ${deal.original_price}
            </span>
          )}
        </div>

        {/* Stats */}
        <div className="flex items-center gap-3 text-xs text-muted-foreground mb-3">
          {deal.watchers_count > 0 && (
            <span className="flex items-center gap-1">
              <Eye className="w-3 h-3" />
              {deal.watchers_count} watching
            </span>
          )}
          {timeLeft && (
            <span className="flex items-center gap-1 text-orange-500">
              <Clock className="w-3 h-3" />
              {timeLeft}
            </span>
          )}
        </div>

        {/* Actions */}
        <div className="flex gap-2">
          <motion.button
            onClick={(e) => { e.stopPropagation(); onSave(); }}
            className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-medium transition-all ${
              deal.is_saved 
                ? 'bg-primary/20 text-primary' 
                : 'bg-muted hover:bg-muted/80'
            }`}
            whileTap={{ scale: 0.95 }}
            data-testid="save-btn"
          >
            <Bookmark className={`w-4 h-4 ${deal.is_saved ? 'fill-current' : ''}`} />
            {deal.is_saved ? 'Saved' : 'Save'}
          </motion.button>
          
          <motion.a
            href={deal.deal_url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-medium text-white transition-all"
            style={{ backgroundColor: categoryColor }}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.95 }}
            data-testid="buy-btn"
          >
            <ExternalLink className="w-4 h-4" />
            Buy Now
          </motion.a>
        </div>

        {/* Website Link */}
        {deal.website_name && (
          <p className="text-xs text-center text-muted-foreground mt-2">
            from <span className="font-medium">{deal.website_name}</span>
          </p>
        )}
      </div>

      {/* Dropper Info */}
      <div className="px-4 pb-4 flex items-center gap-2">
        <div className="w-6 h-6 rounded-full bg-gradient-to-r from-pink-500 to-violet-600 flex items-center justify-center text-xs text-white">
          {deal.user_name?.charAt(0) || '?'}
        </div>
        <span className="text-xs text-muted-foreground">
          Dropped by <span className="font-medium text-foreground">{deal.user_name}</span>
        </span>
      </div>
    </motion.div>
  );
};

// Helper function
function getTimeLeft(expiresAt) {
  const now = new Date();
  const expires = new Date(expiresAt);
  const diff = expires - now;
  
  if (diff <= 0) return 'Expired';
  
  const hours = Math.floor(diff / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  
  if (hours > 24) {
    const days = Math.floor(hours / 24);
    return `${days}d left`;
  }
  if (hours > 0) return `${hours}h ${minutes}m left`;
  return `${minutes}m left`;
}

export default DealCard;
